import { ComponentType } from 'react';
import LegacyJobDetails from '~/components/JobDetails/JobDetails';
import { IJob } from '~/models';
import { JobStatusEnum } from '~/models/Job';
import { CancelledExpiredPage } from './CancelledExpiredPage';
import { CompletedShiftDetailsPage } from './CompletedShiftDetailsPage/CompletedShiftDetailsPage';
import { ConfirmCandidatesPage } from './ConfirmCandidatesPage/ConfirmCandidatesPage';
import { OpenShiftDetailsPage } from './OpenShiftDetailsPage/OpenShiftDetailsPage';
import { ScheduledShiftDetailsPage } from './ScheduledShiftDetailsPage/ScheduledShiftDetailsPage';

/**
 * The Shift Details states the redesign ships as separate pages. Each is built
 * by its own ticket and added to `SHIFT_DETAILS_PAGES`; the shell and the gate
 * stay agnostic of which ones exist yet.
 */
export type TShiftDetailsPageKey =
  | 'open'
  | 'confirm-candidates'
  | 'scheduled'
  | 'completed'
  | 'cancelled-expired';

/** Props every state page receives from the gate. */
export interface IShiftDetailsPageProps {
  job: IJob;
}

/**
 * Job status → redesign page. Mirrors the routing the legacy screens do today:
 * `pending`/`counter` land on Confirm Candidate, a confirmed shift lands on the
 * scheduled view, a finished one on the completed view, and everything terminal
 * is the cancelled/expired view.
 *
 * Statuses with no redesigned page return `null`, which the gate reads as
 * "fall through to the legacy screen" — so a status nobody designed for can
 * never render a blank page.
 *
 * `pay-out` and `adjusted-required` are deliberately absent, so they resolve to
 * `null` and keep the legacy screen. They are not undesigned states: they drive
 * the End of Day, rating and adjustment flows, which already work and which this
 * redesign does not touch. Routing them to the `scheduled` page — as this map did
 * before the COMPLETED page existed — would offer "Message Pro" and "Cancel" on a
 * shift that has already been worked.
 */
export function shiftDetailsPageForStatus(status?: JobStatusEnum): TShiftDetailsPageKey | null {
  switch (status) {
    case JobStatusEnum.Open:
      return 'open';

    case JobStatusEnum.Pending:
    case JobStatusEnum.Counter:
    case JobStatusEnum.PendingCounter:
      return 'confirm-candidates';

    case JobStatusEnum.Filled:
      return 'scheduled';

    case JobStatusEnum.Complete:
      return 'completed';

    case JobStatusEnum.Cancelled:
    case JobStatusEnum.CancelledHyg:
    case JobStatusEnum.CancelledDnt:
    case JobStatusEnum.Expired:
      return 'cancelled-expired';

    default:
      return null;
  }
}

/**
 * The redesign page for this shift, or `null` to keep a legacy screen. The one
 * place that decides which shifts the redesign takes over.
 *
 * Onsite interviews are always `null`, whatever their status: interviews are out
 * of the redesign (design, 2026-09-16). Each state page is shaped for a shift — a
 * money receipt, a rate-and-hours summary, Edit Shift and Invite — while an
 * interview titles itself as one and carries the permanent-hire Hire/Reject
 * decision. The OPEN page also cannot list interview applicants yet:
 * `/eligible-candidates` does not serve the ratings, skills or availability its
 * invite block renders (GOTU-5501, blocked on the backend).
 *
 * Which legacy screen an interview gets is `interviewFallbackForJob`'s answer.
 */
export function shiftDetailsPageForJob(
  job: Pick<IJob, 'status' | 'isOnsiteInterview'>,
): TShiftDetailsPageKey | null {
  if (job.isOnsiteInterview) {
    return null;
  }

  return shiftDetailsPageForStatus(job.status);
}

/**
 * The state pages built so far. A status with no entry here falls through to its
 * legacy screen, flag on or off, so each page ticket activates its own status by
 * registering itself.
 *
 * Populated statically from this module, so the set of live pages is readable in
 * one place. Do not register by import side effect: a page that has to be
 * imported to exist is a page that silently disappears when the import is
 * tree-shaken or reordered.
 */
export const SHIFT_DETAILS_PAGES: Partial<
  Record<TShiftDetailsPageKey, ComponentType<IShiftDetailsPageProps>>
> = {
  open: OpenShiftDetailsPage,
  'confirm-candidates': ConfirmCandidatesPage,
  scheduled: ScheduledShiftDetailsPage,
  completed: CompletedShiftDetailsPage,
  'cancelled-expired': CancelledExpiredPage,
};

/**
 * Legacy screens an onsite interview is sent to by page key, where the route's
 * own legacy child is the wrong screen for that status. A key with no entry keeps
 * the route's child.
 *
 * The route's child is right for most statuses, and better than a screen picked
 * per status: the legacy screens redirect among themselves (the shift route sends
 * a pending interview to its first applicant, a booked or finished one to
 * `/details`), and the one on screen stays mounted when a confirm or decline
 * changes the status under it, so its outcome popup survives. It also keeps the
 * GOTU-5384 experiment resolving for interviews exactly as it does now.
 *
 * The exception is a cancelled or expired interview, which the shift route's
 * `OpenJobScreen` does not redirect: it would show Edit and Cancel on an interview
 * that is over. `JobDetails` renders it as the "Interview Summary" it is — the
 * same component the `/details` route renders, so nothing remounts there.
 */
export const SHIFT_DETAILS_INTERVIEW_FALLBACKS: Partial<
  Record<TShiftDetailsPageKey, ComponentType>
> = {
  'cancelled-expired': LegacyJobDetails,
};

/**
 * The legacy screen the gate renders in place of the route's child for this
 * shift, or `null` to render the route's child. Only ever an onsite interview's:
 * see `SHIFT_DETAILS_INTERVIEW_FALLBACKS`.
 */
export function interviewFallbackForJob(
  job: Pick<IJob, 'status' | 'isOnsiteInterview'>,
): ComponentType | null {
  if (!job.isOnsiteInterview) {
    return null;
  }

  const key = shiftDetailsPageForStatus(job.status);

  return (key && SHIFT_DETAILS_INTERVIEW_FALLBACKS[key]) || null;
}
